import React from "react";

interface CoverageOption {
    value: number;
    label: string;
}

interface CoverageAmountSliderProps {
    options: CoverageOption[];
    value: number;
    onChange: (value: number) => void;
    disabled?: boolean;
}

const CoverageAmountSlider = ({ options, value, onChange, disabled = false }: CoverageAmountSliderProps) => {
    const currentIndex = options.findIndex((opt) => opt.value === value);
    const maxIndex = options.length - 1;
    const mostPopularIndex = options.findIndex((opt) => opt.value === 300000);

    const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const index = parseInt(e.target.value);
        onChange(options[index].value);
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    return (
        <div className="form-control">
            <label className="label h-7" htmlFor="coverage-amount-slider">
                <span className="text-base-content">Coverage Amount</span>
            </label>
            <div>
                <input
                    type="range"
                    min={0}
                    max={maxIndex}
                    value={currentIndex >= 0 ? currentIndex : 0}
                    onChange={handleSliderChange}
                    className="range range-primary"
                    step="1"
                    disabled={disabled}
                    id="coverage-amount-slider"
                    aria-valuenow={currentIndex >= 0 ? options[currentIndex].value : undefined}
                    aria-valuemin={options[0]?.value}
                    aria-valuemax={options[maxIndex]?.value}
                    tabIndex={0}
                />
                <div className="w-full flex justify-between text-xs px-2">
                    {options.map((option, index) => (
                        <span key={`tick-${option.value}-${index}`} className="text-base-content">
                            |
                        </span>
                    ))}
                </div>
                <div className="w-full flex justify-between text-xs px-2">
                    {options.map((option, index) => (
                        <span
                            key={`label-${option.value}-${index}`}
                            className={`${index === currentIndex ? "font-bold text-primary" : "text-base-content"}`}
                        >
                            {option.label || formatCurrency(option.value)}
                        </span>
                    ))}
                </div>
                <div className="w-full flex justify-between px-2 mt-1 relative">
                    {options.map((option, index) => (
                        <div key={`badge-spacer-${option.value}-${index}`} className="flex justify-center relative">
                            {index === mostPopularIndex && (
                                <div className="absolute top-0 left-1/2 -translate-x-1/2">
                                    <div className="bg-warning rounded px-2 py-1 h-7 flex items-center shadow-sm">
                                        <span className="text-base-content text-[11px] font-semibold whitespace-nowrap">
                                            <span>Most popular</span>
                                        </span>
                                    </div>
                                    <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent border-b-warning" />
                                </div>
                            )}
                            {/* Invisible spacer to maintain width */}
                            <span className="invisible text-[120px] tablet:text-[10px]">.</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CoverageAmountSlider;
